// src/SocketContext.js - FINALISÉ avec deleteNotification, connectSocket, disconnectSocket et ANTI-DOUBLON

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { getAuthToken } from './utils/auth';

// --- Configuration ---
const BACKEND_URL = 'https://api.insulated.pro';

// --- Création du Contexte ---
const SocketContext = createContext({
    socket: null,
    notifications: [],
    unreadCount: 0,
    fetchNotifications: () => Promise.resolve(),
    markAllAsRead: () => Promise.resolve(),
    markAsRead: () => Promise.resolve(),
    deleteNotification: () => Promise.resolve(),
    sendEvent: () => {},
    connectSocket: () => {},
    disconnectSocket: () => {},
});

export const useSocket = () => {
    return useContext(SocketContext);
};

export const SocketProvider = ({ children }) => {
    const [socket, setSocket] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);

    const socketRef = useRef(null);

    // ----------------------------------------------------
    // API REST LOGIC
    // ----------------------------------------------------

    const fetchNotifications = useCallback(async () => {
        const token = getAuthToken();
        if (!token) return;

        try {
            const res = await fetch(`${BACKEND_URL}/api/notifications`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await res.json();

            if (res.ok) {
                setNotifications(data);
                const initialUnread = data.filter(n => !n.read).length;
                setUnreadCount(initialUnread);
            } else {
                console.error("Échec du chargement de l'historique:", data);
            }
        } catch (err) {
            console.error("Erreur API lors du chargement des notifications:", err);
        }
    }, []);

    const markAllAsRead = useCallback(async () => {
        const token = getAuthToken();
        if (!token) return;

        try {
            const res = await fetch(`${BACKEND_URL}/api/notifications/read-all`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (res.ok) {
                setNotifications(prevNotifs => prevNotifs.map(n => ({ ...n, read: true })));
                setUnreadCount(0);
            } else {
                console.error("Échec de la mise à jour 'read-all'.");
            }
        } catch (err) {
            console.error("Erreur API lors de la mise à jour 'read-all':", err);
        }
    }, []);

    const markAsRead = useCallback(async (notifId) => {
        const token = getAuthToken();
        if (!token || !notifId) return;

        let notificationWasUnread = false;

        setNotifications(prevNotifs =>
            prevNotifs.map(n => {
                if (n._id === notifId && !n.read) {
                    notificationWasUnread = true;
                    return { ...n, read: true };
                }
                return n;
            })
        );
        if (notificationWasUnread) {
            setUnreadCount(prevCount => Math.max(0, prevCount - 1));
        }

        try {
            const res = await fetch(`${BACKEND_URL}/api/notifications/${notifId}/read`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!res.ok) {
                console.error(`Échec de la mise à jour 'read' pour ${notifId}.`);
            }
        } catch (err) {
            console.error("Erreur API lors de la mise à jour 'read':", err);
        }
    }, []);

    const deleteNotification = useCallback(async (notifId) => {
        const token = getAuthToken();
        if (!token || !notifId) return;

        let wasUnread = false;

        setNotifications(prevNotifs =>
            prevNotifs.filter(n => {
                if (n._id === notifId && !n.read) {
                    wasUnread = true;
                }
                return n._id !== notifId;
            })
        );

        if (wasUnread) {
            setUnreadCount(prevCount => Math.max(0, prevCount - 1));
        }

        try {
            const res = await fetch(`${BACKEND_URL}/api/notifications/${notifId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!res.ok) {
                console.error(`Échec de la suppression pour ${notifId}.`);
            }
        } catch (err) {
            console.error("Erreur API lors de la suppression:", err);
        }
    }, []);

    // ----------------------------------------------------
    // SOCKET.IO LOGIC
    // ----------------------------------------------------

    const disconnectSocket = useCallback(() => {
        console.log('🔌 [SocketContext] Déconnexion du socket...');

        if (socketRef.current) {
            try {
                socketRef.current.removeAllListeners();
                socketRef.current.disconnect();
            } catch (e) {
                console.warn('Erreur lors de la déconnexion socket:', e);
            }
            socketRef.current = null;
        }

        setSocket(null);
        setNotifications([]);
        setUnreadCount(0);

        console.log('✅ [SocketContext] Socket déconnecté + states reset');
    }, []);

    const connectSocket = useCallback(() => {
        const token = getAuthToken();

        if (!token) {
            console.log("Aucun token trouvé. La connexion socket ne sera pas établie.");
            if (socketRef.current) {
                socketRef.current.disconnect();
                socketRef.current = null;
                setSocket(null);
            }
            return () => {};
        }

        if (socketRef.current && socketRef.current.connected) {
            console.log("Socket déjà actif — déconnexion avant reconnexion");
            socketRef.current.removeAllListeners();
            socketRef.current.disconnect();
            socketRef.current = null;
        }

        console.log("Tentative d'établissement d'une nouvelle connexion socket...");

        const newSocket = io(BACKEND_URL, {
            query: { token },
            transports: ['websocket', 'polling'],
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
            forceNew: true,
        });

        setSocket(newSocket);
        socketRef.current = newSocket;

        newSocket.on('connect', () => {
            console.log(`Socket.IO connecté. ID: ${newSocket.id}`);
        });
        newSocket.on('disconnect', (reason) => {
            console.log('Socket.IO déconnecté. Raison :', reason);
        });
        newSocket.on('connect_error', (err) => {
            console.error("Erreur de connexion Socket.IO :", err.message);
        });

        const notificationHandler = (newNotification) => {
            console.log("Nouvelle notification reçue :", newNotification);
            
            setNotifications(prevNotifs => {
                // 🆕 PROTECTION ANTI-DOUBLON : On vérifie si l'ID existe déjà
                const exists = prevNotifs.some(n => n._id === newNotification._id);
                if (exists) {
                    console.log("⚠️ Doublon ignoré :", newNotification._id);
                    return prevNotifs;
                }
                return [newNotification, ...prevNotifs];
            });

            if (!newNotification.read) {
                setUnreadCount(prevCount => {
                    // Éviter de compter le même doublon deux fois
                    return prevCount + 1;
                });
            }
        };

        // 🆕 ON ÉCOUTE LES DEUX ÉVÉNEMENTS (le doublon est géré par le "exists" au-dessus)
        newSocket.on("notification", notificationHandler);
        newSocket.on("newNotification", notificationHandler);

        return () => {
            console.log('Nettoyage des écouteurs de socket.');
            newSocket.off("notification", notificationHandler);
            newSocket.off("newNotification", notificationHandler);
            newSocket.off('connect');
            newSocket.off('disconnect');
            newSocket.off('connect_error');
        };
    }, []);

    // ----------------------------------------------------
    // Effets principaux
    // ----------------------------------------------------

    useEffect(() => {
        const cleanupConnection = connectSocket();
        return cleanupConnection;
    }, [connectSocket]);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    const sendEvent = useCallback((event, data) => {
        if (socketRef.current && socketRef.current.connected) {
            socketRef.current.emit(event, data);
            console.log(`Événement envoyé: ${event}`);
        } else {
            console.warn("Socket non connecté. Impossible d'envoyer l'événement:", event);
        }
    }, []);

    const contextValue = {
        socket,
        notifications,
        unreadCount,
        fetchNotifications,
        markAllAsRead,
        markAsRead,
        deleteNotification,
        sendEvent,
        connectSocket,
        disconnectSocket,
    };

    return (
        <SocketContext.Provider value={contextValue}>
            {children}
        </SocketContext.Provider>
    );
};